/**
 * L'armure : ce que la portée dit une fois pour toutes, et ce qu'il reste à
 * écrire devant les notes.
 *
 * ## Pourquoi une armure, alors qu'on pourrait signer chaque note
 *
 * Parce qu'en mi♭ majeur il faudrait signer *chaque* si♭, mi♭ et la♭ du
 * morceau. Sur une portée dont les notes sont des barres et non des têtes, un
 * signe devant chacune ferait plus d'encre que de musique. L'armure est ce qui
 * rend les altérations payables : elle prend en charge les altérations de la
 * tonalité, et il ne reste à graver que les **écarts**.
 *
 * ## L'écart, et sa mémoire
 *
 * Un signe vaut jusqu'à la fin de sa mesure, pour sa ligne et pour elle seule :
 * c'est la règle du solfège, et elle divise encore le compte. Un `fa♯` signé au
 * premier temps se rejoue sans signe au troisième ; un `fa` naturel après lui,
 * dans la même mesure, redemande un bécarre.
 *
 * La mémoire est **par main**. Une altération posée sur la portée grave ne vaut
 * pas sur l'aiguë, même à la même mesure : ce sont deux portées, et un pianiste
 * lit la sienne.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne choisit pas l'orthographe — il la lit. Le bémol ou le dièse d'une note
 * a été tranché bien avant, par l'accord ou par la tonalité, dans
 * `windowToneName` ; ce module ne fait que dire lequel des deux il faut
 * *écrire*, sachant ce que l'armure a déjà dit.
 */

import type { Accidental } from "@/lib/harmony/glyphs";
import { MAJOR_TONALITIES, MINOR_TONALITIES, type Tonality } from "@/lib/music/tonality";

/**
 * L'avance d'un signe d'armure, en interlignes.
 *
 * Bravura ne donne aucune chasse : la largeur d'avance de ses altérations est
 * exactement celle de leur boîte, et c'est à celui qui grave d'espacer. Sans
 * lui, deux dièses voisins gardent huit dixièmes de pixel de blanc pour trente
 * -neuf pixels de haut, et comme l'armure zigzague, leurs boîtes se croisent
 * verticalement sur la moitié de leur hauteur : les sept dièses se lisent
 * comme une tache.
 *
 * Les valeurs sont celles de l'usage gravé : le dièse s'espace largement, le
 * bémol se serre — son crochet est en haut à droite et sa panse en bas à
 * gauche, si bien que deux bémols voisins s'emboîtent au lieu de se heurter.
 */
const SIGNATURE_ADVANCE_SPACES = { sharp: 1.3, flat: 1.05 } as const;

/** L'avance d'un signe d'armure, en degrés — l'unité de la portée. */
export function signatureAdvanceSteps(accidental: "sharp" | "flat"): number {
  return SIGNATURE_ADVANCE_SPACES[accidental] * 2;
}

/** Une armure : de quel signe, et combien. */
export type KeySignature = {
  readonly accidental: "sharp" | "flat";
  /** De 0 à 7. Zéro est une armure vide, et non l'absence d'armure. */
  readonly count: number;
};

/** Les deux clés du piano, qui ne posent pas l'armure à la même place. */
export type Clef = "treble" | "bass";

/**
 * L'ordre des dièses, puis celui des bémols, en lettres.
 *
 * `fa do sol ré la mi si` et son miroir `si mi la ré sol do fa`. Les lettres
 * sont indexées comme partout ailleurs, `do` valant zéro.
 */
const SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6] as const;
const FLAT_ORDER = [6, 2, 5, 1, 4, 0, 3] as const;

/**
 * Les degrés des sept dièses, comptés depuis la ligne du bas de la portée.
 *
 * `fa♯` sur la ligne du haut de la portée de sol, et le reste en découle : la
 * règle d'usage veut qu'aucun signe ne sorte de la portée par le haut, ce qui
 * fait redescendre le `la♯` d'une octave.
 */
const SHARPS_ABOVE_BOTTOM = [8, 5, 9, 6, 3, 7, 4] as const;

/** Les degrés des sept bémols, même repère. `si♭` sur la troisième ligne. */
const FLATS_ABOVE_BOTTOM = [4, 7, 3, 6, 2, 5, 1] as const;

/**
 * De combien de degrés l'armure de la clé de fa descend sous celle de sol.
 *
 * De deux, ce qui n'est pas la distance entre les deux portées : l'armure
 * grave se lit une tierce plus bas *sur sa propre portée*, et c'est la
 * convention de n'importe quelle partition de piano. Le `fa♯` est sur la ligne
 * du haut en clé de sol, sur la quatrième en clé de fa.
 */
const BASS_DROP = 2;

/**
 * L'armure d'une tonalité, ou `null` quand on n'en connaît pas.
 *
 * Lue sur le cycle des quintes que `tonality.ts` tient déjà : sept crans à
 * droite de do sont sept dièses, les cinq suivants sont cinq à un bémols. Une
 * seconde table ici finirait par diverger de celle-là.
 */
export function keySignatureOf(tonality: Tonality | null): KeySignature | null {
  if (tonality === null) {
    return null;
  }

  const circle = tonality.mode === "major" ? MAJOR_TONALITIES : MINOR_TONALITIES;
  const rank = circle.findIndex((entry) => entry.tonic === tonality.tonic);

  if (rank < 0) {
    return null;
  }

  return rank <= 6
    ? { accidental: "sharp", count: rank }
    : { accidental: "flat", count: circle.length - rank };
}

/**
 * Les degrés où l'armure pose ses signes, dans l'ordre où on les grave.
 *
 * Vide sur une armure vide, ce qui est le cas de do majeur et de la mineur :
 * une portée sans armure n'a rien à écrire, et ce n'est pas une portée sans
 * tonalité.
 */
export function signatureSteps(
  signature: KeySignature | null,
  clef: Clef,
  bottomStep: number,
): readonly number[] {
  if (signature === null) {
    return [];
  }

  const above = signature.accidental === "sharp" ? SHARPS_ABOVE_BOTTOM : FLATS_ABOVE_BOTTOM;
  const drop = clef === "bass" ? BASS_DROP : 0;

  return above.slice(0, signature.count).map((offset) => bottomStep + offset - drop);
}

/** L'altération que l'armure donne d'office à la lettre d'un degré. */
export function alterInKey(step: number, signature: KeySignature | null): number {
  if (signature === null || signature.count === 0) {
    return 0;
  }

  const letter = ((step % 7) + 7) % 7;
  const order = signature.accidental === "sharp" ? SHARP_ORDER : FLAT_ORDER;
  const rank = order.indexOf(letter as (typeof order)[number]);

  return rank >= 0 && rank < signature.count ? (signature.accidental === "sharp" ? 1 : -1) : 0;
}

/** Le signe qui écrit une altération, `natural` valant zéro. */
export function accidentalOf(alter: number): Accidental | null {
  switch (alter) {
    case 0:
      return "natural";
    case 1:
      return "sharp";
    case -1:
      return "flat";
    case 2:
      return "doubleSharp";
    case -2:
      return "doubleFlat";
    default:
      return null;
  }
}

/**
 * Le scribe d'une portée : ce qu'il faut écrire devant chaque note, ou rien.
 *
 * Il garde la mesure en cours et ce qu'il y a déjà signé. Appelé dans l'ordre
 * des frappes de **sa** portée, ce qui est l'ordre dans lequel un lecteur les
 * rencontre — la règle de persistance n'a pas d'autre sens que celui-là.
 */
export function accidentalScribe(
  signature: KeySignature | null,
): (strike: { readonly bar: number; readonly step: number; readonly alter: number }) => Accidental | null {
  let bar: number | null = null;
  const written = new Map<number, number>();

  return ({ bar: at, step, alter }) => {
    if (at !== bar) {
      bar = at;
      written.clear();
    }

    const standing = written.get(step) ?? alterInKey(step, signature);

    if (alter === standing) {
      return null;
    }

    written.set(step, alter);

    return accidentalOf(alter);
  };
}
